import { Injectable } from '@angular/core';
import { PeopleService } from './people.service';
import { Person } from '../models/index';
import { BaseService } from './baseService';
import { ApplicationType } from '../../utils';

enum AuthFlag {
  forbidden = -100,
  blanck = 0,
  authorized = 100
}

@Injectable({
  providedIn: 'root'
})
export class AuthorizationService extends BaseService {
  private actions: any;
  private permissions: any;
  private user: Person;

  constructor(private peopleService: PeopleService) {
    super();
    this.peopleService.getPermissions().subscribe((data) => {
      this.user = data;
      this.permissions = data ? data.authorizations : null;
    });

    this.actions = {
      'inspection.comment': (entity) => {
        return entity ? entity.permissions['inspection.comment'] === AuthFlag.authorized : false;
      },
      'inspection.validate': (entity) => {
        return entity
          ? entity.isDone() &&
              (this.getFlag('manage.inspection', this.permissions) === AuthFlag.authorized ||
                this.getFlag('inspection.validate', this.permissions) === AuthFlag.authorized)
          : false;
      },
      'inspection.delete': (entity) => {
        return entity
          ? this.getFlag('manage.inspection', this.permissions) === AuthFlag.authorized || entity.owner === this.user.id
          : false;
      },
      'inspection.collaborate': (entity) => {
        return entity
          ? this.getFlag('manage.inspection', this.permissions) === AuthFlag.authorized || entity.owner === this.user.id
          : false;
      },
      'manage.inspections': (entity) => {
        return this.getFlag('manage.inspection', this.permissions) === AuthFlag.authorized;
      },
      'inspection.parts.quotation': (entity) => {
        return this.getFlag('inspection.parts.quotation', this.permissions) === AuthFlag.authorized;
      },
      'inspection.add-plugin': (entity) => {
        return entity ? entity.owner === this.user.id : false;
      },
      'manage.people': (entity) => {
        return this.getFlag('manage.people', this.permissions) === AuthFlag.authorized;
      },
      'manage.template': (entity) => {
        return this.getFlag('manage.template', this.permissions) === AuthFlag.authorized;
      },
      'manage.inspection': (entity) => {
        return this.getFlag('manage.inspection', this.permissions) === AuthFlag.authorized;
      },
      'manage.inspection.status': (entity) => {
        return this.getFlag('manage.inspection.status', this.permissions) === AuthFlag.authorized;
      },
      offline: (entity) => {
        return this.getFlag('offline', this.permissions) === AuthFlag.authorized;
      },
      'inspection.offline.unlock': (entity) => {
        return (
          this.getFlag('offline', this.permissions) === AuthFlag.authorized &&
          this.getFlag('manage.inspections', this.permissions) === AuthFlag.authorized
        );
      },
      'manage.inspection.delete': (entity) => {
        return entity
          ? entity.owner === this.user.id ||
              this.getFlag('manage.inspection.delete', this.permissions) === AuthFlag.authorized
          : false;
      },
      'inspection.offline.edit': (entity) => {
        if (this.isManager) {
          return true;
        }
        if (entity) {
          if (entity.status > 200) return false;

          if (this.isManager && (entity.isOffline || entity.isSynchronizing)) {
            return false;
          }
          if (entity.isOffline) {
            return entity.offlinePersonId === this.user.id;
          }
          if (entity.isSynchronizing) {
            return false;
          }
          if (!entity.isOffline) {
            return this.isConnection;
          }
        }
      },
      'inspection.offline.menu': (entity) => {
        if (entity) {
          if (entity.isOffline && entity.offlinePersonId === this.user.id) {
            return true;
          }
          if (entity.isOffline && this.isConnection && this.isManager) {
            return this.getFlag('manage.inspection', this.permissions) === AuthFlag.authorized;
          }
          if (entity.isSynchronizing) {
            return false;
          }
          if (!entity.isOffline) {
            return this.isConnection;
          }
        }
      },
      'manage.parts': (entity) => {
        return this.getFlag('manage.inspection', this.permissions) === AuthFlag.authorized || this.user.isHost;
      },
      'actions.restore': (entity) => {
        return this.getFlag('actions.restore', this.permissions) === AuthFlag.authorized || this.user.isHost;
      },
      host: (entity) => {
        return this.user.isHost;
      },
      'application.maintenance': (entity) => this.applicationType === ApplicationType.Maintenance,
      'application.optimization': (entity) => this.applicationType === ApplicationType.Optimization,
      'application.installation': (entity) => this.applicationType === ApplicationType.Installation
    };
  }

  public isAuthorize(action, entity?): boolean {
    if (this.user) {
      return this.actions[action] && this.actions[action](entity);
    } else {
      this.peopleService.getCurrent().subscribe((el) => {
        this.user = el;
        this.permissions = this.user.authorizations;
        return this.actions[action] && this.actions[action](entity);
      });
    }
  }

  private getFlag(action: string, permissions) {
    const actions = this.getActions(action);
    const permission = permissions.Items.find((p) =>
      actions.some((a) => {
        return a.toLowerCase() === p.ActionName.toLowerCase();
      })
    );
    return permission ? permission.Flag : AuthFlag.blanck;
  }

  private getActions(action: string): string[] {
    const result = [action, `${action}.*`];
    const splitActions = action.split('.').map((a) => a.trim());
    splitActions.splice(splitActions.length - 1, 1);
    const length = splitActions.length;
    for (let index = 0; index < length; index++) {
      result.push(`${splitActions.join('.')}.*`);
      splitActions.splice(splitActions.length - 1, 1);
    }
    result.push('*');
    return result;
  }
}
